import type {
  DescriptionTemplate,
  Eta,
  GenerationPhase,
  StyleOption,
} from '../types/domain';

export const MUSIC_STYLES: StyleOption[] = [
  { name: 'Поп', icon: '🎵' },
  { name: 'Шансон', icon: '❤️' },
  { name: 'Рок', icon: '🎸' },
  { name: 'Рэп / Хип-хоп', icon: '🎤' },
  { name: 'Танцевальный', icon: '🪩' },
  { name: 'Romantic', icon: '❤️' },
  { name: 'Весёлый', icon: '✨' },
  { name: 'Диско 90-х', icon: '🪩' },
  { name: 'Мощный', icon: '⚡' },
  { name: 'Глубокий басс', icon: '🌊' },
  { name: 'Грустный', icon: '🌧️' },
  { name: 'Баллада', icon: '❤️' },
  { name: 'Народная', icon: '🌲' },
  { name: 'Электро', icon: '⚡' },
  { name: 'Акустика', icon: '🎶' },
  { name: 'Романс', icon: '❤️' },
  { name: 'Хаус', icon: '📻' },
  { name: 'Классика', icon: '✨' },
  { name: 'Эпичный', icon: '✨' },
  { name: 'Джаз', icon: '🎹' },
  { name: 'Симфонический оркестр', icon: '✨' },
  { name: 'Детская песня', icon: '🎵' },
  { name: 'R&B', icon: '🌊' },
  { name: 'Регги', icon: '🌲' },
  { name: 'Инди', icon: '🌧️' },
  { name: 'Патриотический', icon: '⚡' },
  { name: 'Мюзикл', icon: '✨' },
  { name: 'Lo-Fi', icon: '🎧' },
];

/** Theme templates for "Тема песни" — sent to the backend as `theme`. */
export const DESCRIPTION_TEMPLATES: DescriptionTemplate[] = [
  { text: 'День рождения', emoji: '🎂' },
  { text: 'Любовь', emoji: '❤️' },
  { text: 'Разлука', emoji: '💔' },
  { text: 'Лето', emoji: '☀️' },
  { text: 'Мечта', emoji: '✨' },
];

export const ITEMS_PER_PAGE = 5;

export const GENERATION_PHASE_LABELS: Record<GenerationPhase, string> = {
  preparing_voice: 'Подготавливаем ваш голос для песни...',
  queued: 'Задача поставлена в очередь...',
  awaiting_generation: 'Ожидаем свободные мощности для генерации...',
  generating: 'Нейросеть создаёт музыку...',
};

export const GENERATION_PHASE_PROGRESS: Record<GenerationPhase, number> = {
  preparing_voice: 10,
  queued: 25,
  awaiting_generation: 35,
  generating: 65,
};

export const GENERATION_PHASE_CEILING: Record<GenerationPhase, number> = {
  preparing_voice: 24,
  queued: 34,
  awaiting_generation: 60,
  generating: 88,
};

export const GENERATION_PHASE_TAU_MS: Record<GenerationPhase, number> = {
  preparing_voice: 20_000,
  queued: 6_000,
  awaiting_generation: 12_000,
  generating: 45_000,
};

export function getGenerationPhaseLabel(phase: GenerationPhase): string {
  return GENERATION_PHASE_LABELS[phase] ?? 'Генерация песни...';
}

export function parseIsoDurationSeconds(iso: string): number {
  const match =
    /^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/.exec(iso);
  if (!match) return 0;
  const [, days, hours, minutes, seconds] = match;
  return (
    (Number(days) || 0) * 86400 +
    (Number(hours) || 0) * 3600 +
    (Number(minutes) || 0) * 60 +
    (Number(seconds) || 0)
  );
}

export function formatDurationRu(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  if (seconds < 60) return `${seconds} сек.`;

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} мин.`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes
    ? `${hours} ч ${remainingMinutes} мин.`
    : `${hours} ч`;
}

/** Renders an Eta as a human-readable Russian range, e.g. "~1 мин." or "от 30 сек. до 2 мин." */
export function formatEta(eta: Eta): string {
  const p50 = formatDurationRu(parseIsoDurationSeconds(eta.p50));
  const p90 = formatDurationRu(parseIsoDurationSeconds(eta.p90));
  return p50 === p90 ? `~${p50}` : `от ${p50} до ${p90}`;
}

export function getTrackTitle(index: number): string {
  return `Версия ${index + 1}`;
}
